import { WarningIcon } from '@assets/icons/minor/WarningIcon';
import { TrendingDownIcon } from '@assets/icons/TrendingDownIcon';
import { TrendingUpIcon } from '@assets/icons/TrendingUpIcon';
import { cn } from '@components/shadcn/utils';
import { ImageryButton } from '@components/shared/ImageryButton/ImageryButton';
import { useNotificationsCenter } from '@contexts/NotificationsCenterContext';
import { useSystemLanguage } from '@contexts/SystemLanguageContext';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Amplitude } from 'src/amplitude';
import type { NotificationCenter } from '../../NotificationCenter.models';
import { buildReportUrl } from '../../reportUrl.utils';
import { BaseNotification } from '../BaseNotification';
import { useNdviThresholdPhrases } from './NdviThresholdNotification.hooks';
import {
  buildFarmPath,
  buildFieldPath,
  NDVI_CHANGE_REPORT_PATH,
} from './NdviThresholdNotification.utils';

function NdviThresholdAction({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const { t } = useTranslation();
  const [appLanguage] = useSystemLanguage();
  const navigate = useNavigate();
  const { markAsRead, setPopoverVisible } = useNotificationsCenter();
  const { content, details } = useNdviThresholdPhrases(notification);

  const reportKey = content?.report_key;
  const fieldId = content?.field_id;

  const farmPath = buildFarmPath(content);
  const fieldPath = buildFieldPath(content);

  const eventProperties = useMemo(
    () => ({
      scenario: content?.scenario,
      direction: content?.direction,
      threshold: content?.threshold,
      fields_count: content?.fields_count,
      farms_count: content?.farms_count,
    }),
    [
      content?.scenario,
      content?.direction,
      content?.threshold,
      content?.fields_count,
      content?.farms_count,
    ]
  );

  const openReport = useCallback(() => {
    Amplitude.trackNdviThresholdOpenReport(eventProperties);
    window.open(
      buildReportUrl({ path: NDVI_CHANGE_REPORT_PATH, reportKey, appLanguage }),
      '_blank'
    );
    markAsRead(notification);
  }, [reportKey, appLanguage, notification, markAsRead, eventProperties]);

  // Navigating in-app hides the popover, otherwise it covers the destination.
  const navigateTo = useCallback(
    (path: string) => {
      setPopoverVisible(false);
      markAsRead(notification);
      navigate(path);
    },
    [setPopoverVisible, markAsRead, notification, navigate]
  );

  if (!content) {
    return null;
  }

  const hasActions = Boolean(reportKey || farmPath);

  return (
    <React.Fragment>
      <p className="text-sm text-gray-700 whitespace-pre-line mt-1">{details}</p>
      {hasActions && (
        <div className="flex flex-wrap gap-3 mt-2">
          {fieldPath && (
            <ImageryButton
              type="softDefault"
              onClick={() => {
                Amplitude.trackNdviThresholdGoToField(eventProperties);
                navigateTo(fieldPath);
              }}
            >
              {t('notifications.actions.go_to_field')}
            </ImageryButton>
          )}
          {farmPath && !fieldId && (
            <ImageryButton
              type="softDefault"
              onClick={() => {
                Amplitude.trackNdviThresholdGoToFarm(eventProperties);
                navigateTo(farmPath);
              }}
            >
              {t('notifications.actions.go_to_farm')}
            </ImageryButton>
          )}
          {reportKey && (
            <ImageryButton type="softPrimary" onClick={openReport}>
              {t('notifications.actions.open_report')}
            </ImageryButton>
          )}
        </div>
      )}
    </React.Fragment>
  );
}

function NdviThresholdNotification({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const { content, title } = useNdviThresholdPhrases(notification);
  const direction = content?.direction;

  const icon = (
    <div
      data-testid={`ndvi-threshold-thumb-${direction ?? 'default'}`}
      className={cn('p-1 rounded-lg', {
        ['bg-sy-green-10']: direction === 'above',
        ['bg-sy-red-10']: direction === 'below',
        ['bg-sy-blue-10']: !direction,
      })}
    >
      {direction === 'above' && <TrendingUpIcon />}
      {direction === 'below' && <TrendingDownIcon />}
      {!direction && <WarningIcon width={24} height={24} />}
    </div>
  );

  return (
    <BaseNotification
      notification={notification}
      icon={icon}
      title={title}
      ActionComponent={NdviThresholdAction}
    />
  );
}

export { NdviThresholdNotification };
